/**
 * "EBITDA Bridge" tab body for the FinalMappingCard — the P&L-only adjusted
 * EBITDA waterfall.
 *
 * On mount it does a read-only GET /agent/sessions/{id}/ebitda-bridge. If none
 * has been generated yet (404 -> null) it POSTs the same endpoint automatically
 * to run the single Opus classification pass (~20-60s) — no button, same
 * click-the-tab contract as RatiosTab. The in-flight POST is registered in
 * generationJobs, so clicking away unmounts the view but the request finishes
 * in the background (the server persists the result); revisiting the tab
 * re-attaches to the running promise or picks the finished result up from the
 * server cache.
 *
 * Layout: one per-period table. Add-backs are the rows (an explicit include/
 * exclude check-vs-X toggle plus the account name in the first column, the
 * signed impact per period in the rest, green raises EBITDA / red lowers it).
 * Click a row to expand its reasoning in a height-animated panel. The footer
 * carries Reported EBITDA, the per-period Difference %, and Adjusted EBITDA,
 * aligned to the same period columns. Only included add-backs feed Adjusted
 * EBITDA; toggling recomputes locally and PATCHes the approved set so it
 * persists per session.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  Clock,
  Loader2,
  RefreshCw,
} from 'lucide-react';

import {
  generateAgentEbitdaBridge,
  getAgentEbitdaBridge,
  updateAgentEbitdaBridgeApprovals,
} from '../../utils/agentApi';
import { getInflightJob, getJobError, startJob } from '../../utils/generationJobs';
import './EBITDABridgeTab.css';

// ---- response shape (mirrors agent/ebitda_bridge/schemas.py) ----

interface PeriodRef {
  period_index: number;
  display_name: string;
}

interface SpikeEvidence {
  period_index: number;
  display_name: string;
  z_score: number;
  excess: number;
}

interface AddBack {
  id: string;
  account_name: string;
  row_index: number;
  category: string | null;
  section: string;
  adjustment_type: string;
  direction: string;
  tier: number;
  requires_input: boolean;
  rationale: string;
  confidence: number;
  spike_evidence: SpikeEvidence[];
  // The account's own raw values per period (aligned to `periods`), so the row
  // can chart the trend. Distinct from `impact_by_period` (the signed EBITDA
  // effect). Absent on bridges generated before this field shipped.
  values_by_period: number[];
  impact_by_period: number[];
  annual_impact: number;
  approved: boolean;
}

interface BridgePeriod {
  period_index: number;
  display_name: string;
  revenue: number;
  reported_ebitda: number;
  adjusted_ebitda: number;
}

interface BridgeResult {
  periods: PeriodRef[];
  add_backs: AddBack[];
  bridge: BridgePeriod[];
  reconciliation: { status?: string };
  notes: string[];
}

type Status = 'loading' | 'generating' | 'ready' | 'error';

interface BridgeModel {
  periods: PeriodRef[];
  addBacks: AddBack[];
  reported: number[];
  adjusted: number[];
}

interface Props {
  sessionId: string;
}

// ---- formatting helpers ----

const round = (v: number | null | undefined): number | null =>
  v == null || Number.isNaN(v) ? null : Math.round(v);

// Absolute magnitude, negatives in parens (accounting style).
const fmtMoney = (v: number | null | undefined): string => {
  const r = round(v);
  if (r == null) return '—';
  const s = Math.abs(r).toLocaleString('en-US');
  return r < 0 ? `(${s})` : s;
};

// Signed, leading + so a raise vs a cut is unmistakable; 0 reads as a dash.
const fmtSigned = (v: number | null | undefined): string => {
  const r = round(v);
  if (r == null || r === 0) return '—';
  const s = Math.abs(r).toLocaleString('en-US');
  return r < 0 ? `(${s})` : `+${s}`;
};

const fmtPct = (v: number | null | undefined): string =>
  v == null || Number.isNaN(v) ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}%`;

const signClass = (v: number | null | undefined): string => {
  const r = round(v);
  if (r == null || r === 0) return 'eb-zero';
  return r > 0 ? 'eb-pos' : 'eb-neg';
};

// "X% above/below avg" for the trend tooltip; mirrors the QoE candidate view.
const cmpToAvg = (v: number, mean: number): string => {
  if (mean === 0 || v === 0) return '';
  if (v > mean) return `${Math.round((v / mean - 1) * 100)}% above avg`;
  if (v < mean) return `${Math.round((1 - v / mean) * 100)}% below avg`;
  return 'At average';
};

const reconWarn = (status?: string): boolean =>
  Boolean(status && !['pass', 'reconciled', 'passed', 'ok'].includes(status.toLowerCase()));

// Toggleable only if it actually moves the number; an unsized estimate is shown
// for context but cannot be included until it has an amount.
const includable = (ab: AddBack): boolean =>
  (ab.impact_by_period || []).some((v) => Math.round(v) !== 0);

const seedApproved = (d: BridgeResult | null): Set<string> =>
  new Set((d?.add_backs || []).filter((ab) => ab.approved).map((ab) => ab.id));

// Per-add-back trend: the account's own raw values across the periods, drawn as
// narrow flat bars on a crisp zero baseline. Sign shows as which side of the
// baseline a bar sits on; the flagged period(s) are coral and everything else is
// a quiet slate (so spike, negative, and average never blur into one red). The
// average is parked in the header. Self-contained hover state so each row owns
// its own tooltip. Renders nothing when the bridge predates `values_by_period`
// (old cached result) or the account is all zeros.
const AddBackTrend = ({ ab, periods }: { ab: AddBack; periods: PeriodRef[] }) => {
  const [hover, setHover] = useState<number | null>(null);

  const values = periods.map((_, i) => ab.values_by_period?.[i] ?? 0);
  const nonZero = values.filter((v) => v !== 0);
  if (nonZero.length === 0) return null;

  const maxAbs = Math.max(...values.map((v) => Math.abs(v)), 1);
  const hasNeg = Math.min(...values, 0) < 0;
  const mean = nonZero.reduce((a, b) => a + b, 0) / nonZero.length;
  const showAvg = mean > 0 && nonZero.length > 1;
  // Dashed-line height, shared by the line inside the plot and the avg pill in
  // the gutter to its right, so the two always sit at the same level.
  const avgBottom = hasNeg
    ? `${50 + (mean / maxAbs) * 50}%`
    : `${(mean / maxAbs) * 100}%`;

  // Flag the backend's authoritative (leave-one-out) spikes, plus any bar well
  // above the run-rate so a lumpy one-time item with no z-score spike still reads
  // as the anomaly. The flag drives color; the sign drives position only.
  const spikeIdx = new Set<number>();
  (ab.spike_evidence || []).forEach((s) => {
    const i = periods.findIndex((p) => p.period_index === s.period_index);
    if (i >= 0) spikeIdx.add(i);
  });
  const isFlag = (v: number, i: number): boolean =>
    v !== 0 &&
    (spikeIdx.has(i) || (nonZero.length > 1 && Math.abs(v) > Math.abs(mean) * 1.5));

  const n = periods.length;
  // Keep the tooltip off the chart edges (translateX(-50%) would clip at 0/100%).
  const tipLeft = hover == null ? 0 : Math.min(92, Math.max(8, ((hover + 0.5) / n) * 100));
  const enter = (i: number) => () => setHover(i);
  const leave = (i: number) => () => setHover((h) => (h === i ? null : h));

  return (
    <div className="ebt-trend">
      <div className="ebt-grid">
        <div className="ebt-grid-head">Period breakdown</div>
        <div className="ebt-grid-list">
          {periods.map((p, i) => {
            const v = values[i];
            const has = v !== 0;
            return (
              <div
                key={p.period_index}
                className={`ebt-grid-row ${hover === i ? 'hl' : ''} ${
                  isFlag(v, i) ? 'flag' : ''
                }`}
                onMouseEnter={enter(i)}
                onMouseLeave={leave(i)}
              >
                <span className="ebt-grid-idx">{i + 1}</span>
                <span className="ebt-grid-name" title={p.display_name}>
                  {p.display_name}
                </span>
                <span
                  className={`ebt-grid-val ${v < 0 ? 'neg' : ''} ${has ? '' : 'muted'}`}
                >
                  {has ? fmtMoney(v) : '–'}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="ebt-chart">
        <div className="ebt-chart-head">Trend analysis</div>
        <div className="ebt-plot-row">
          <div className={`ebt-bar-chart ${hasNeg ? 'has-neg' : ''}`}>
            <div className="ebt-plot">
              <div className="ebt-bars-area">
                {hasNeg && <div className="ebt-zero-line" />}
                {showAvg && (
                  <div className="ebt-mean-line" style={{ bottom: avgBottom }} />
                )}
                {hover != null && (
                  <div className="ebt-tip" style={{ left: `${tipLeft}%` }}>
                    <div className="ebt-tip-name">{periods[hover].display_name}</div>
                    <div className="ebt-tip-val">
                      {values[hover] === 0 ? '–' : fmtMoney(values[hover])}
                    </div>
                    {cmpToAvg(values[hover], mean) && (
                      <div className="ebt-tip-cmp">{cmpToAvg(values[hover], mean)}</div>
                    )}
                  </div>
                )}
                <div className="ebt-bars">
                  {values.map((v, i) => {
                    const h = (Math.abs(v) / maxAbs) * 100;
                    const neg = v < 0;
                    return (
                      <div
                        key={periods[i].period_index}
                        className={`ebt-bar-wrap ${neg ? 'neg-wrap' : 'pos-wrap'} ${
                          hover === i ? 'hl' : ''
                        }`}
                        onMouseEnter={enter(i)}
                        onMouseLeave={leave(i)}
                      >
                        <div
                          className={`ebt-bar ${isFlag(v, i) ? 'flag' : ''} ${
                            v === 0 ? 'zero' : ''
                          }`}
                          style={{ height: `${Math.max(h, v === 0 ? 2 : 4)}%` }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="ebt-bar-labels">
                {periods.map((p, i) => (
                  <span
                    key={p.period_index}
                    className={`ebt-bar-label ${hover === i ? 'hl' : ''}`}
                  >
                    {i + 1}
                  </span>
                ))}
              </div>
            </div>
          </div>
          {showAvg && (
            <div className="ebt-avg-gutter">
              <span className="ebt-mean-label" style={{ bottom: avgBottom }}>
                avg {fmtMoney(mean)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EBITDABridgeTab = ({ sessionId }: Props) => {
  const [status, setStatus] = useState<Status>('loading');
  const [data, setData] = useState<BridgeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [approved, setApproved] = useState<Set<string>>(() => new Set<string>());
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set<string>());
  const [saveError, setSaveError] = useState<string | null>(null);

  const jobKey = `ebitda-bridge:${sessionId}`;

  useEffect(() => {
    let cancelled = false;

    const attach = (job: Promise<BridgeResult>) => {
      setStatus('generating');
      setError(null);
      setSaveError(null);
      job
        .then((d: BridgeResult) => {
          if (cancelled) return;
          setData(d);
          setApproved(seedApproved(d));
          setExpanded(new Set<string>());
          setStatus('ready');
        })
        .catch((err: Error) => {
          if (!cancelled) {
            setError(err.message);
            setStatus('error');
          }
        });
    };

    // A generation kicked off on an earlier visit may still be running —
    // re-attach instead of re-firing.
    const running = getInflightJob<BridgeResult>(jobKey);
    if (running) {
      attach(running);
      return () => {
        cancelled = true;
      };
    }
    // A generation failed while the tab was closed: show the error, don't
    // silently re-fire the LLM pass. Try again clears it.
    const failed = getJobError(jobKey);
    if (failed) {
      setError(failed);
      setStatus('error');
      return () => {
        cancelled = true;
      };
    }

    setStatus('loading');
    setError(null);
    getAgentEbitdaBridge(sessionId)
      .then((d: BridgeResult | null) => {
        if (cancelled) return;
        if (d) {
          setData(d);
          setApproved(seedApproved(d));
          setStatus('ready');
        } else {
          // Nothing cached server-side: generation starts the moment the tab
          // opens. The job outlives this component (see generationJobs).
          attach(
            startJob(jobKey, () =>
              generateAgentEbitdaBridge(sessionId, { force: false }),
            ),
          );
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message);
          setStatus('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const run = (force: boolean) => {
    setStatus('generating');
    setError(null);
    setSaveError(null);
    startJob(jobKey, () => generateAgentEbitdaBridge(sessionId, { force }))
      .then((d: BridgeResult) => {
        setData(d);
        setApproved(seedApproved(d));
        setExpanded(new Set<string>());
        setStatus('ready');
      })
      .catch((err: Error) => {
        setError(err.message);
        setStatus('error');
      });
  };

  const toggle = (ab: AddBack) => {
    if (!includable(ab)) return;
    const next = new Set(approved);
    if (next.has(ab.id)) next.delete(ab.id);
    else next.add(ab.id);
    setApproved(next);
    setSaveError(null);
    updateAgentEbitdaBridgeApprovals(sessionId, Array.from(next)).catch((err: Error) => {
      setApproved(approved); // revert so the number never lies about what is saved
      setSaveError(err.message || 'Could not save your change.');
    });
  };

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const model = useMemo<BridgeModel | null>(() => {
    if (!data) return null;
    const periods = data.periods || [];
    const addBacks = data.add_backs || [];
    const reported = (data.bridge || []).map((b) => b.reported_ebitda ?? 0);
    const adjusted = reported.map((rep, i) =>
      addBacks.reduce(
        (s, ab) => s + (approved.has(ab.id) ? ab.impact_by_period?.[i] ?? 0 : 0),
        rep,
      ),
    );
    return { periods, addBacks, reported, adjusted };
  }, [data, approved]);

  if (status === 'loading') {
    return (
      <div className="eb-state">
        <Loader2 size={16} className="eb-spin" />
        <span>Loading…</span>
      </div>
    );
  }

  if (status === 'generating') {
    return (
      <div className="eb-state">
        <Loader2 size={18} className="eb-spin" />
        <span>Normalizing EBITDA from the P&amp;L. This takes a moment.</span>
      </div>
    );
  }

  if (status === 'error' || !model) {
    return (
      <div className="eb-state eb-state-error">
        <AlertTriangle size={16} />
        <span>{error || 'Could not generate the bridge.'}</span>
        <button type="button" className="eb-btn" onClick={() => run(false)}>
          <RefreshCw size={13} /> Try again
        </button>
      </div>
    );
  }

  const { periods, addBacks, reported, adjusted } = model;
  const ncol = periods.length + 1;
  // Only add-backs that actually move EBITDA belong in the impact table. Timing
  // and reclass items net to zero across the periods, so they would render as a
  // row of dashes; surface them separately as flags instead.
  const realAddBacks = addBacks.filter(includable);
  const timingFlags = addBacks.filter((ab) => !includable(ab));

  return (
    <div className="eb-wrap">
      <div className="eb-head">
        <div className="eb-head-text">
          <span className="eb-title">Adjusted EBITDA bridge</span>
          <span className="eb-sub">
            Proposed from the P&amp;L. Toggle the add-backs you accept; only those
            count toward Adjusted EBITDA.
          </span>
        </div>
        <button type="button" className="eb-btn eb-btn-ghost" onClick={() => run(true)}>
          <RefreshCw size={12} /> Re-run
        </button>
      </div>

      {reconWarn(data?.reconciliation?.status) && (
        <div className="eb-note eb-note-warn">
          <AlertTriangle size={13} /> This mapping did not fully reconcile to
          source Net Income, so reported EBITDA may be off.
        </div>
      )}
      {saveError && (
        <div className="eb-note eb-note-warn">
          <AlertTriangle size={13} /> {saveError}
        </div>
      )}
      {realAddBacks.length === 0 && (
        <div className="eb-note">
          No add-backs change the number. Adjusted EBITDA equals reported EBITDA.
        </div>
      )}

      <div className="eb-table-wrap">
        <table className="eb-table">
          <thead>
            <tr>
              <th className="eb-col-label">Add-back</th>
              {periods.map((p) => (
                <th key={p.period_index} className="eb-col-num">
                  {p.display_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {realAddBacks.map((ab) => {
              const on = approved.has(ab.id);
              const can = includable(ab);
              const open = expanded.has(ab.id);
              return (
                <React.Fragment key={ab.id}>
                  <tr className={`eb-ab-tr ${on ? 'is-on' : 'is-off'}`}>
                    <td className="eb-col-label">
                      <div className="eb-namecell">
                        <button
                          type="button"
                          role="switch"
                          className={`eb-switch ${on ? 'is-on' : 'is-off'}`}
                          onClick={() => toggle(ab)}
                          disabled={!can}
                          aria-checked={on}
                          title={
                            can
                              ? on
                                ? 'Included. Click to exclude.'
                                : 'Excluded. Click to include.'
                              : 'Needs an amount before it can be included'
                          }
                        >
                          <span className="eb-knob" />
                        </button>
                        <button
                          type="button"
                          className="eb-name-btn"
                          onClick={() => toggleExpand(ab.id)}
                          aria-expanded={open}
                        >
                          <span className="eb-ab-name">{ab.account_name}</span>
                          <ChevronDown size={14} className={`eb-chev ${open ? 'is-open' : ''}`} />
                        </button>
                      </div>
                    </td>
                    {(ab.impact_by_period || []).map((v, i) => (
                      <td key={i} className={`eb-col-num ${signClass(v)}`}>
                        {fmtSigned(v)}
                      </td>
                    ))}
                  </tr>
                  <tr className="eb-detail-tr" aria-hidden={!open}>
                    <td colSpan={ncol} className="eb-detail-td">
                      <div className={`eb-detail-grid ${open ? 'is-open' : ''}`}>
                        <div className="eb-detail-inner">
                          <div className="eb-detail-body">
                            {ab.rationale && <p className="eb-ab-why">{ab.rationale}</p>}
                            {ab.requires_input && (
                              <div className="eb-ab-meta">
                                <span className="eb-tag">Estimate, off until you confirm</span>
                              </div>
                            )}
                            <AddBackTrend ab={ab} periods={periods} />
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="eb-row-reported">
              <th scope="row" className="eb-col-label">
                Reported EBITDA
              </th>
              {reported.map((v, i) => (
                <td key={i} className={`eb-col-num ${v < 0 ? 'eb-neg' : ''}`}>
                  {fmtMoney(v)}
                </td>
              ))}
            </tr>
            <tr className="eb-row-diff">
              <th scope="row" className="eb-col-label">
                Difference %
              </th>
              {periods.map((p, i) => {
                const d = adjusted[i] - reported[i];
                // No add-back touched this period: show a neutral dash, not a
                // green +0.0% that reads like a real (positive) change.
                const noChange = Math.round(d) === 0;
                // Percent of the ABSOLUTE base so negative-EBITDA periods
                // still show a magnitude; sign/color follow the direction of
                // the change (signClass(d)), not the raw division. Only a
                // zero base stays a dash.
                const pct =
                  !noChange && reported[i] !== 0
                    ? d / Math.abs(reported[i])
                    : null;
                return (
                  <td
                    key={i}
                    className={`eb-col-num ${noChange ? 'eb-zero' : signClass(d)}`}
                  >
                    {pct == null ? '—' : fmtPct(pct)}
                  </td>
                );
              })}
            </tr>
            <tr className="eb-row-adjusted">
              <th scope="row" className="eb-col-label">
                Adjusted EBITDA
              </th>
              {adjusted.map((v, i) => (
                <td key={i} className={`eb-col-num ${v < 0 ? 'eb-neg' : ''}`}>
                  {fmtMoney(v)}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>

      {timingFlags.length > 0 && (
        <div className="eb-flags">
          <div className="eb-flags-head">
            <Clock size={14} className="eb-flags-icon" />
            <span className="eb-flags-title">Timing &amp; reclass flags</span>
            <span className="eb-flags-sub">no effect on adjusted EBITDA</span>
          </div>
          <ul className="eb-flags-list">
            {timingFlags.map((ab) => (
              <li key={ab.id} className="eb-flag">
                <span className="eb-flag-name">{ab.account_name}</span>
                {ab.rationale && <span className="eb-flag-why">{ab.rationale}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="eb-foot">
        <span className="eb-disclaimer">
          Proposed add-backs, not a signed quality-of-earnings opinion.
        </span>
      </div>
    </div>
  );
};

export default EBITDABridgeTab;
